import classNames from 'clsx';
import React, { memo, ReactElement, useCallback, useEffect } from 'react';
import { Button } from 'reactApp/ui/Button/Button';
import Content from 'reactApp/ui/Content/Content';
import { CopyWeblink } from 'reactApp/ui/CopyWeblink/CopyWeblink';
import Dialog from 'reactApp/ui/Dialog/Dialog';
import { noop } from 'reactApp/utils/helpers';
import opener from 'reactApp/utils/opener';

import styles from './WhatsNewDialog.css';

export interface IProps {
    primaryButtonText: string;
    primaryButtonHref?: string;
    secondaryButtonText?: string;
    secondaryButtonHref?: string;
    date?: number;
    title: string;
    text: string;
    imageUrl: string;
    imageBgColor?: string;
    imageFluid?: boolean;
    closable?: boolean;
    link?: string;
    onShow?(): void;
    onClose?(): void;
    onClick?(): void;
    onSecondary?(): void;
}

export const WhatsNewDialog = memo(function WhatsNewDialog({
    primaryButtonText,
    secondaryButtonHref,
    primaryButtonHref,
    onClick = noop,
    onSecondary = noop,
    onClose = noop,
    onShow = noop,
    secondaryButtonText,
    date = 0,
    title,
    text,
    link,
    closable = true,
    imageUrl,
    imageBgColor,
    imageFluid = true,
}: IProps): ReactElement {
    useEffect(() => onShow(), []);

    const onPrimaryHandler = useCallback(() => {
        onClick();
        if (primaryButtonHref) {
            opener(primaryButtonHref, true);
        }
    }, [onClick, primaryButtonHref]);

    const onSecondaryHandler = useCallback(() => {
        onSecondary();
        if (secondaryButtonHref) {
            opener(secondaryButtonHref, true);
        }
    }, [onSecondary, secondaryButtonHref]);

    return (
        <Dialog
            open
            header={''}
            closeOnDimmerClick={false}
            id="whatsnew-dlg"
            onCancel={onClose}
            size="large"
            bigCloseIcon
            closable={closable}
            className={styles.root}
        >
            <Content isModal>
                <div className={styles.content}>
                    <div
                        className={styles.imageWrapper}
                        style={{
                            backgroundColor: imageBgColor,
                        }}
                    >
                        <img
                            className={classNames(styles.image, {
                                [styles.image_fluid]: imageFluid,
                            })}
                            src={imageUrl}
                            alt="Cover image"
                        />
                    </div>
                    {Boolean(date) && <div className={styles.date}>{date}</div>}
                    <div className={styles.title}>{title}</div>
                    <div className={styles.text}>{text.replace(/\\n/g, '\n')}</div>

                    <div className={styles.buttonWrapper}>
                        {primaryButtonText && (
                            <Button onClick={onPrimaryHandler} data-name="success" theme="octavius" primary middle>
                                {primaryButtonText}
                            </Button>
                        )}
                        {secondaryButtonText && (
                            <Button onClick={onSecondaryHandler} theme="octavius" data-name="secondary" middle>
                                {secondaryButtonText}
                            </Button>
                        )}
                        {link && (
                            <div className={styles.copyWeblink}>
                                <CopyWeblink publicUrl={link} type="new" onCopy={onPrimaryHandler} />
                            </div>
                        )}
                    </div>
                </div>
            </Content>
        </Dialog>
    );
});
